import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid, ToggleButton, ToggleButtonGroup,
  FormControlLabel, Switch, useTheme, useMediaQuery,
} from '@mui/material';
import { ArrowBack, Payment, Person, Business } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeService, contractorService, farmOperationService, projectService,
    apiClient
} from '@smartgrow/shared';
import type { PaymentMethod, OperationTask } from '@smartgrow/shared';

type PaymentType = 'SALARY' | 'CONTRACTOR';

const today = new Date().toISOString().split('T')[0];
const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

export const NewPaymentPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [type, setType] = useState<PaymentType>('SALARY');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useExistingContractor, setUseExistingContractor] = useState(true);
  const [selectedOpId, setSelectedOpId] = useState('');
  const [tasks, setTasks] = useState<OperationTask[]>([]);

  // Salary form state
  const [salaryForm, setSalaryForm] = useState({
    employeeId: '',
    amount: '' as number | '',
    periodMonth: currentMonth,
    periodYear: currentYear,
    paymentDate: today,
    paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
    referenceNumber: '',
    notes: '',
    farmOperationId: undefined as string | undefined,
    operationTaskId: undefined as string | undefined,
  });

  // Contractor form state
  const [contractorForm, setContractorForm] = useState({
    contractorId: undefined as string | undefined,
    contractorName: '',
    contractorContact: '',
    description: '',
    amount: '' as number | '',
    paymentDate: today,
    paymentMethod: 'BANK_TRANSFER' as PaymentMethod,
    referenceNumber: '',
    notes: '',
    farmOperationId: undefined as string | undefined,
    operationTaskId: undefined as string | undefined,
  });

  const { data: employeeData } = useQuery({
    queryKey: ['employees-active'],
    queryFn: () => employeeService.getEmployees({ size: 200 }),
  });

  const { data: contractorData } = useQuery({
    queryKey: ['contractors-active'],
    queryFn: () => contractorService.getContractors({ size: 200 }),
  });

  const [selectedProjectId, setSelectedProjectId] = useState('');

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['farm-operations', selectedProjectId],
    queryFn: () => farmOperationService.getByProject(selectedProjectId),
    enabled: !!selectedProjectId,
  });
  const operations = operationsData?.content ?? [];

  const employees = employeeData?.content ?? [];
  const contractors = (contractorData?.content ?? []).filter((c) => c.isActive);
  const projects = projectsData?.content ?? [];

  useEffect(() => {
    if (selectedOpId) {
      farmOperationService.getTasksByOperation(selectedOpId).then(setTasks);
    } else {
      setTasks([]);
    }
  }, [selectedOpId]);

  const setSalary = (field: string, value: unknown) => {
    setSalaryForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  const setContractor = (field: string, value: unknown) => {
    setContractorForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  const salaryMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/payments/salary', {
        employeeId: salaryForm.employeeId,
        amount: Number(salaryForm.amount),
        periodMonth: salaryForm.periodMonth,
        periodYear: salaryForm.periodYear,
        paymentDate: salaryForm.paymentDate,
        paymentMethod: salaryForm.paymentMethod,
        referenceNumber: salaryForm.referenceNumber || undefined,
        notes: salaryForm.notes || undefined,
        farmOperationId: salaryForm.farmOperationId,
        operationTaskId: salaryForm.operationTaskId,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payments'] }); navigate('/dashboard/payments'); },
  });

  const contractorMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/payments/contractor', {
        contractorId: useExistingContractor ? contractorForm.contractorId : undefined,
        contractorName: useExistingContractor ? undefined : contractorForm.contractorName,
        contractorContact: useExistingContractor ? undefined : contractorForm.contractorContact || undefined,
        description: contractorForm.description,
        amount: Number(contractorForm.amount),
        paymentDate: contractorForm.paymentDate,
        paymentMethod: contractorForm.paymentMethod,
        referenceNumber: contractorForm.referenceNumber || undefined,
        notes: contractorForm.notes || undefined,
        farmOperationId: contractorForm.farmOperationId,
        operationTaskId: contractorForm.operationTaskId,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payments'] }); navigate('/dashboard/payments'); },
  });

  const isPending = salaryMutation.isPending || contractorMutation.isPending;
  const isError = salaryMutation.isError || contractorMutation.isError;

  const validate = () => {
    const e: Record<string, string> = {};
    if (type === 'SALARY') {
      if (!salaryForm.employeeId) e.employeeId = 'Employee required';
      if (!salaryForm.amount || Number(salaryForm.amount) <= 0) e.amount = 'Amount required';
      if (!salaryForm.paymentDate) e.paymentDate = 'Date required';
    } else {
      if (useExistingContractor && !contractorForm.contractorId) e.contractorId = 'Select contractor';
      if (!useExistingContractor && !contractorForm.contractorName.trim()) e.contractorName = 'Name required';
      if (!contractorForm.description.trim()) e.description = 'Description required';
      if (!contractorForm.amount || Number(contractorForm.amount) <= 0) e.amount = 'Amount required';
      if (!contractorForm.paymentDate) e.paymentDate = 'Date required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (type === 'SALARY') salaryMutation.mutate();
    else contractorMutation.mutate();
  };

  const FarmLinkSection = ({ opId, setOpId, opTaskId, setOpTaskId }: {
    opId: string; setOpId: (v: string) => void;
    opTaskId: string | undefined; setOpTaskId: (v: string | undefined) => void;
  }) => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Link to Farm Operation (optional — for cost tracking)
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField select fullWidth label="Project" value={selectedProjectId}
          onChange={(e) => {
            setSelectedProjectId(e.target.value);
            setOpId('');
            setOpTaskId(undefined);
            if (type === 'SALARY') setSalary('farmOperationId', undefined);
            else setContractor('farmOperationId', undefined);
          }}>
          <MenuItem value="">— No project —</MenuItem>
          {projects.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField select fullWidth label="Farm Operation" value={opId}
          disabled={!selectedProjectId}
          helperText={!selectedProjectId ? 'Select a project first' : undefined}
          onChange={(e) => {
            const val = e.target.value;
            setOpId(val);
            setOpTaskId(undefined);
            if (type === 'SALARY') setSalary('farmOperationId', val || undefined);
            else setContractor('farmOperationId', val || undefined);
          }}>
          <MenuItem value="">— None —</MenuItem>
          {operations.map((op) => (
            <MenuItem key={op.id} value={op.id}>
              {op.name || op.operationType.replace(/_/g, ' ')}
              {op.cropVariety ? ` (${op.cropVariety})` : ''}
              {op.animalType ? ` (${op.animalType})` : ''}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {tasks.length > 0 && (
        <Grid item xs={12} md={6}>
          <TextField select fullWidth label="Task (optional)" value={opTaskId ?? ''}
            onChange={(e) => {
              const val = e.target.value || undefined;
              setOpTaskId(val);
              if (type === 'SALARY') setSalary('operationTaskId', val);
              else setContractor('operationTaskId', val);
            }}>
            <MenuItem value="">— No specific task —</MenuItem>
            {tasks.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
          </TextField>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size={isMobile ? 'small' : 'large'}>Back</Button>
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">New Payment</Typography>
          {!isMobile && <Typography variant="body2" color="text.secondary">Process a direct payment to an employee or contractor</Typography>}
        </Box>
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 3 }}>Payment failed. Check details and try again.</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Payment Type</Typography>
        <ToggleButtonGroup value={type} exclusive
          onChange={(_, val) => { if (val) { setType(val); setErrors({}); } }}
          size={isMobile ? 'small' : 'large'}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ width: isMobile ? '100%' : 'auto' }}>
          <ToggleButton value="SALARY" sx={{ px: { xs: 2, sm: 4 }, gap: 1 }}><Person /> Salary Payment</ToggleButton>
          <ToggleButton value="CONTRACTOR" sx={{ px: { xs: 2, sm: 4 }, gap: 1 }}><Business /> Contractor Payment</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {type === 'SALARY' && (
        <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Person color="primary" />
            <Typography variant="h6" fontWeight="bold">Salary Details</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Employee *" value={salaryForm.employeeId}
                onChange={(e) => {
                  const emp = employees.find((x) => x.id === e.target.value);
                  setSalaryForm((f) => ({ ...f, employeeId: e.target.value, amount: emp ? emp.monthlySalary : f.amount }));
                }}
                error={!!errors.employeeId} helperText={errors.employeeId || 'Auto-fills monthly salary'}>
                <MenuItem value="">Select employee</MenuItem>
                {employees.filter((e) => e.isActive).map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.fullName} ({e.employeeNumber}) — ₦{e.monthlySalary.toLocaleString()}/mo
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Amount (₦) *" value={salaryForm.amount}
                onChange={(e) => setSalary('amount', Number.parseFloat(e.target.value) || '')}
                error={!!errors.amount} helperText={errors.amount} inputProps={{ min: 0.01, step: 100 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Period Month *" value={salaryForm.periodMonth}
                onChange={(e) => setSalary('periodMonth', Number(e.target.value))}>
                {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Period Year *" value={salaryForm.periodYear}
                onChange={(e) => setSalary('periodYear', Number.parseInt(e.target.value))}
                inputProps={{ min: 2020, max: currentYear + 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Payment Date *" value={salaryForm.paymentDate}
                onChange={(e) => setSalary('paymentDate', e.target.value)}
                error={!!errors.paymentDate} helperText={errors.paymentDate} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Payment Method *" value={salaryForm.paymentMethod}
                onChange={(e) => setSalary('paymentMethod', e.target.value as PaymentMethod)}>
                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
                <MenuItem value="CHEQUE">Cheque</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reference Number (optional)" value={salaryForm.referenceNumber}
                onChange={(e) => setSalary('referenceNumber', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Notes (optional)" value={salaryForm.notes}
                onChange={(e) => setSalary('notes', e.target.value)} inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid item xs={12}>
              <FarmLinkSection
                opId={selectedOpId}
                setOpId={(v) => { setSelectedOpId(v); setSalary('farmOperationId', v || undefined); }}
                opTaskId={salaryForm.operationTaskId}
                setOpTaskId={(v) => setSalary('operationTaskId', v)}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {type === 'CONTRACTOR' && (
        <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Business color="primary" />
            <Typography variant="h6" fontWeight="bold">Contractor Payment Details</Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={useExistingContractor} onChange={(e) => setUseExistingContractor(e.target.checked)} />}
            label="Use existing contractor"
            sx={{ mb: 2 }}
          />
          <Grid container spacing={3}>
            {useExistingContractor ? (
              <Grid item xs={12}>
                <TextField select fullWidth label="Contractor *" value={contractorForm.contractorId ?? ''}
                  onChange={(e) => setContractor('contractorId', e.target.value)}
                  error={!!errors.contractorId} helperText={errors.contractorId}>
                  <MenuItem value="">Select contractor</MenuItem>
                  {contractors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.companyName} ({c.contractorCode})
                      {c.specialty ? ` — ${c.specialty}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Contractor Name *" value={contractorForm.contractorName}
                    onChange={(e) => setContractor('contractorName', e.target.value)}
                    error={!!errors.contractorName} helperText={errors.contractorName} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Contact (optional)" value={contractorForm.contractorContact}
                    onChange={(e) => setContractor('contractorContact', e.target.value)} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Description *" value={contractorForm.description}
                onChange={(e) => setContractor('description', e.target.value)}
                error={!!errors.description} helperText={errors.description}
                placeholder="e.g. Foundation work milestone 2" inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Amount (₦) *" value={contractorForm.amount}
                onChange={(e) => setContractor('amount', Number.parseFloat(e.target.value) || '')}
                error={!!errors.amount} helperText={errors.amount} inputProps={{ min: 0.01, step: 100 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Payment Date *" value={contractorForm.paymentDate}
                onChange={(e) => setContractor('paymentDate', e.target.value)}
                error={!!errors.paymentDate} helperText={errors.paymentDate} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Payment Method *" value={contractorForm.paymentMethod}
                onChange={(e) => setContractor('paymentMethod', e.target.value as PaymentMethod)}>
                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
                <MenuItem value="CHEQUE">Cheque</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reference Number (optional)" value={contractorForm.referenceNumber}
                onChange={(e) => setContractor('referenceNumber', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Notes (optional)" value={contractorForm.notes}
                onChange={(e) => setContractor('notes', e.target.value)} inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid item xs={12}>
              <FarmLinkSection
                opId={selectedOpId}
                setOpId={(v) => { setSelectedOpId(v); setContractor('farmOperationId', v || undefined); }}
                opTaskId={contractorForm.operationTaskId}
                setOpTaskId={(v) => setContractor('operationTaskId', v)}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4, flexDirection: isMobile ? 'column' : 'row' }}>
        <Button size="large" onClick={() => navigate('/dashboard/payments')} disabled={isPending} fullWidth={isMobile}>Cancel</Button>
        <Button size="large" variant="contained" color="success" onClick={handleSubmit}
          disabled={isPending} fullWidth={isMobile}
          startIcon={isPending ? <CircularProgress size={20} /> : <Payment />} sx={{ minWidth: isMobile ? 'unset' : 180 }}>
          {isPending ? 'Processing...' : 'Process Payment'}
        </Button>
      </Box>
    </Box>
  );
};
